import { Injectable } from '@angular/core';
import { FileItem, FileService } from '@app/core/services/file.service';
import { DataTaskService } from '@app/core/services/data-task.service';
import { PrintService } from '@app/core/services/print.service';
import { PdfService } from './pdf.service';
import { HtmlReportService } from './html-report.service';
import { Base64UrlService } from './base64url.service';

@Injectable({
    providedIn: 'root',
})
export class ReportService {
    constructor(private datatask: DataTaskService, private base64: Base64UrlService, private print: PrintService, private file: FileService, private pdf: PdfService, private htmlReport: HtmlReportService) {}

    /**
     * Retrieves the report definition for the given report params.
     *
     * @datatask 334
     * @datataskName sp_ws_p2_p2reportdef_get
     * @params params used to retrieve the definition.
     * @returns promise containing the report definition.
     */
    public async getP2ReportDef(params: P2ReportDefParams): Promise<P2ReportDefGet> {
        return (await this.datatask.Post(334, params))[0];
    }

    /**
     * Generates and retrieves the report file for telerik report with the given report id.
     *
     * @param reportId the id of the telerik report to generate and retrieve file for.
     * @param parameters parameters to use for the generating.
     * @param instanceId instance id to send with http call.
     * @param suppressErrorMessage whether to suppress error message.
     * @returns promise containing the file item.
     */
    public async getTelerikReport(reportId: string, parameters: { [key: string]: unknown }, instanceId?: string, suppressErrorMessage?: boolean): Promise<FileItem | null> {
        try {
            const data: PrintArchiveGet = await this.print.printService.archive({
                reportId: reportId,
                parameters: parameters,
            });

            return await this.getFileDetailsWithMultipleAttempts(data.fileNameWithExtension, instanceId, suppressErrorMessage);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /**
     * Generates and saves the report to file server and retrieves the generated file.
     *
     * @param p2ReportDefKeyno the keyno of the html report to generate, save and retrieve file for.
     * @param jsonParams the params to use for the generating.
     * @param instanceId instance id to send with http call.
     * @param suppressErrorMessage whether to suppress error message.
     * @returns  promise containing the file item.
     */
    public async getHTMLReport(p2ReportDefKeyno: number, jsonParams: string, instanceId?: string, suppressErrorMessage?: boolean): Promise<FileItem | null> {
        try {
            const p2ReportDef = (await this.pdf.loadP2ReportDef(p2ReportDefKeyno))[0];
            const htmlReport = (await this.htmlReport.getReport(p2ReportDef.htmlreport_keyno))[0];

            const parms = {
                htmlreport_keyno: htmlReport.htmlreport_keyno,
                jsonwhere: jsonParams,
            };

            const htmlReportHtml = (await this.htmlReport.getHtmlReportHtml(parms))[0];

            const htmlparms = {
                sourceHtml: this.base64.urlDecode(htmlReportHtml.htmlstring),
                filename: this.base64.urlDecode(htmlReportHtml.filename),
                saveToFileServer: true,
                pdfOptions: {
                    marginOptions: { bottom: '12.7mm', top: '12.7mm', left: '12.7mm', right: '12.7mm' },
                    landscape: p2ReportDef.print_landscape == '1' ? true : false,
                    outline: false,
                    printBackground: true,
                    displayHeaderFooter: true,
                    headerTemplate: this.base64.urlDecode(htmlReportHtml.headerstring),
                    footerTemplate: this.base64.urlDecode(htmlReportHtml.footerstring),
                } as any,
            };

            if (p2ReportDef.paper_size_id !== 'standard' && p2ReportDef.height > 0 && p2ReportDef.width > 0) {
                htmlparms.pdfOptions.height = `${p2ReportDef.height}mm`;
                htmlparms.pdfOptions.width = `${p2ReportDef.width}mm`;

                htmlparms.pdfOptions.marginOptions = { bottom: '5mm', top: '5mm', left: '5mm', right: '5mm' };
            }

            const retval = await this.pdf.htmlToPdfBlob(htmlparms.sourceHtml, htmlparms);

            return await this.getFileDetailsWithMultipleAttempts(retval.fileNameWithExtension, instanceId, suppressErrorMessage);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    private async getFileDetailsWithMultipleAttempts(fileName: string, instanceId?: string, suppressErrorMessage?: boolean) {
        try {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return await this.file.getFileDetails({ fileName: fileName, instanceId: instanceId, suppressErrorMessage: suppressErrorMessage });
        } catch (error) {
            try {
                await new Promise((resolve) => setTimeout(resolve, 5000));
                return await this.file.getFileDetails({ fileName: fileName, instanceId: instanceId, suppressErrorMessage: suppressErrorMessage });
            } catch (error) {
                throw error;
            }
        }
    }
}

export interface PrintArchiveGet {
    fileName: string;
    fileNameWithExtension: string;
}

export interface P2ReportDefParams {
    p2_reportdef_keyno: `${number}` | number;
}
export interface P2ReportDefGet {
    p2_reportdef_keyno: `${number}` | number;
    report_type_keyno: `${number}` | number;
    htmlreport_keyno: `${number}` | number;
    word_id_report_name: string;
    word_id_report_description: string;
    word_id_report_name_translated: string;
    word_id_report_description_translated: string;
    report_filename: string;
    report_filename_template: string;
    p2_webpage_keyno: `${number}` | number;
    print_landscape: '1' | '0';
    isLandscape: string;
    enabled: '1' | '0';
    paper_size_id: string;
    width: `${number}` | number;
    height: `${number}` | number;
    show_retrievearguments: '1' | '0';
    show_arg_datefomtom: '1' | '0';
    show_arg_businessco_no: '1' | '0';
    current_portal_user_keyno: `${number}` | number;
    edi_type_keyno: `${number}` | number;
    default_printer: `${number}` | number;
    printserver_keyno: `${number}` | number;
    is_published: '1' | '0';
    show_is_published: '1' | '0';
    custom_margins: '1' | '0';
    margin_top: `${number}` | number;
    margin_right: `${number}` | number;
    margin_bottom: `${number}` | number;
    margin_left: `${number}` | number;
    doccategory_keyno: `${number}` | number;
}
